
import { isEmpty, size } from 'es-toolkit/compat';
import valid from 'card-validator';

import CVCInput from '../../components/credit-card/CVCInput';
import Checkbox from '../../components/inputs/Checkbox';
import CreditCardExpiryInput from '../../components/credit-card/CreditCardExpiryInput';
import CreditCardNumberInput from '../../components/credit-card/CreditCardNumberInput';
import Field from '../../components/forms/Field';
import Fields from '../../components/forms/Fields';
import TextInput from '../../components/inputs/DeprecatedTextInput';

interface Props {
  autoComplete?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  fields: {
    cvc: ReduxFormField<string>;
    expiry: ReduxFormField<string>;
    name: ReduxFormField<string>;
    number: ReduxFormField<string>;
    zipCode: ReduxFormField<string>;
    skipAvsCheck?: ReduxFormField<boolean>;
  };
  showFieldLabels?: boolean;
  label?: string;
  hint?: string;
  canSkipAvsCheck?: boolean;
}

export interface CreditCardFields {
  cvc: string;
  expiry: string;
  name: string;
  number: string;
  zipCode: string;
  skipAvsCheck: boolean;
}

export function validateCreditCardForm(
  values: CreditCardFields,
  options?: {
    canSkipAvsCheck?: boolean;
  }
) {
  const { cvc, expiry, name, number, zipCode, skipAvsCheck } = values;
  const errors: ReduxFormErrors<CreditCardFields> = {};

  if (window.braintreeHostedField) {
    const hostedFieldState = window.braintreeHostedField.getState();
    return hostedFieldState.fields.cvv.isValid ? {} : { cvc: 'Your security code is invalid.' };
  }

  const numberValidation = valid.number(number);

  if (!numberValidation.isValid) {
    errors.number = 'Your card number is invalid.';
  }

  const expiryValidation = valid.expirationDate(expiry);

  if (!expiryValidation.isValid) {
    errors.expiry = 'Your expiration date is invalid.';
  }

  const cvvValidation = valid.cvv(
    cvc,
    numberValidation.card ? numberValidation.card.code.size : undefined
  );

  if (!cvvValidation.isValid) {
    errors.cvc = 'Your security code is invalid.';
  }

  if (size((name ?? '').trim()) === 0) {
    errors.name = "Please enter the cardholder's name.";
  }

  if (!(options && options.canSkipAvsCheck && skipAvsCheck) && size((zipCode ?? '').trim()) === 0) {
    errors.zipCode = 'Please enter a Zip/Postal code.';
  }

  if (zipCode && size((zipCode ?? '').trim()) > 9) {
    errors.zipCode = 'Zip/Postal code must contain no more than 9 characters.';
  }

  return errors;
}

export function asyncValidateCreditCardForm(
  values: CreditCardFields,
  options?: {
    canSkipAvsCheck?: boolean;
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    const errors = validateCreditCardForm(values, options);

    if (isEmpty(errors)) {
      resolve();
    } else {
      reject(errors);
    }
  });
}

/**
 * Form for handling credit card inputs
 */
const CreditCardForm = ({
  fields: { name, cvc, expiry, number, zipCode, skipAvsCheck },
  hint,
  label,
  autoComplete,
  disabled,
  canSkipAvsCheck,
  showFieldLabels,
  ...rest
}: Props) => {
  const numberValidation = valid.number(number.value);
  const cvcLength = numberValidation.card ? numberValidation.card.code.size : 4;

  return (
    <Field className="FSHide" data-testid={rest['data-testid']} label={label}>
      <Field
        label={showFieldLabels ? 'Name' : undefined}
        labelFor={showFieldLabels ? 'name' : undefined}
        field={name}
      >
        <TextInput
          id="name"
          autoComplete={autoComplete ? 'name' : 'off'}
          autoCorrect="off"
          disabled={disabled}
          field={name}
          placeholder={'Name on card'}
          data-testid="credit-card-name-input"
        />
      </Field>

      <CreditCardNumberInput
        autoComplete={autoComplete}
        data-testid="credit-card-number-input"
        disabled={disabled}
        field={number}
        label={showFieldLabels ? 'Card number' : undefined}
        id="number"
      />

      <Fields hint={hint}>
        <CreditCardExpiryInput
          autoComplete={autoComplete}
          disabled={disabled}
          field={expiry}
          label={showFieldLabels ? 'Expiration date' : undefined}
          id="expiry"
          data-testid="credit-card-expiry-input"
        />

        <CVCInput
          autoComplete={autoComplete}
          data-testid="credit-card-cvc-input"
          disabled={disabled}
          field={cvc}
          maxLength={cvcLength}
          label={showFieldLabels ? 'Security code' : undefined}
          id="cvc"
        />

        <Field
          field={zipCode}
          label={showFieldLabels ? 'Zip/Postal code' : undefined}
          labelFor={showFieldLabels ? 'zipCode' : undefined}
        >
          <TextInput
            autoComplete={autoComplete ? 'postal-code' : 'off'}
            autoCorrect="off"
            data-testid="credit-card-zipcode-input"
            disabled={disabled}
            field={zipCode}
            placeholder="Zip/Postal code"
          />
        </Field>
      </Fields>
      {!zipCode.value && canSkipAvsCheck && (
        <Field field={skipAvsCheck}>
          <Checkbox
            label="I agree to accept the risk of increased fraud and chargebacks for payments without a Zip/Postal code."
            disabled={disabled}
            field={skipAvsCheck}
            data-testid="accept-tos-checkbox"
          />
        </Field>
      )}
    </Field>
  );
};

export default CreditCardForm;
